import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
// Minimal coordinate presence
const CoordinateGrid = () => <div className="absolute inset-0 w-full h-full opacity-[0.015] pointer-events-none" style={{
  backgroundImage: 'linear-gradient(#F5F5F5 0.5px, transparent 0.5px), linear-gradient(90deg, #F5F5F5 0.5px, transparent 0.5px)',
  backgroundSize: '80px 80px'
}} aria-hidden="true" data-id="element-159" />;
// Mock data structure for past scans
interface Scan {
  id: string;
  type: 'self' | 'work' | 'relationship' | 'child';
  name: string;
  names?: {
    person1: string;
    person2: string;
  }; // for relationship
  dateGenerated: string;
  status: 'full';
}
export function Archive() {
  const [scans, setScans] = useState<Scan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    // Simulate loading past scans from storage/API
    // In production, this would fetch from your backend
    const loadScans = () => {
      // Mock data - replace with actual data fetching
      const mockScans: Scan[] = [
        // {
        //   id: 'scan-001',
        //   type: 'self',
        //   name: 'John Doe',
        //   dateGenerated: '2024-01-15',
        //   status: 'full',
        // },
        // {
        //   id: 'scan-002',
        //   type: 'relationship',
        //   names: { person1: 'John Doe', person2: 'Jane Smith' },
        //   name: 'John Doe & Jane Smith',
        //   dateGenerated: '2024-01-10',
        //   status: 'full',
        // },
      ];
      setScans(mockScans);
      setIsLoading(false);
    };
    setTimeout(loadScans, 300); // Simulate loading delay
  }, []);
  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'self':
        return 'Self System';
      case 'work':
        return 'Work System';
      case 'relationship':
        return 'Relationship';
      case 'child':
        return 'Child System';
      default:
        return 'Diagnostic';
    }
  };
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'self':
        return '🧬';
      case 'work':
        return '💼';
      case 'relationship':
        return '💞';
      case 'child':
        return '🧒';
      default:
        return '📄';
    }
  };
  const getReportPath = (scan: Scan) => {
    switch (scan.type) {
      case 'work':
        return `/report/work/${scan.id}`;
      case 'relationship':
        return `/report/relationship/${scan.id}`;
      case 'child':
        return `/report/child/${scan.id}`;
      default:
        return `/report/${scan.id}`;
    }
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  return <>
      <Helmet data-id="element-160">
        <title data-id="element-161">Archive | Decoder</title>
        <meta name="robots" content="noindex, nofollow" data-id="element-162" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-163">
        {/* Navigation */}
        <header data-id="element-164">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-165">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-166">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-167">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-168" />
                <span className="text-base font-mono tracking-tight text-gray-400" data-id="element-169">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-170">
                <Link to="/decode" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-171">
                  Decoder
                </Link>
                <Link to="/careers" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-172">
                  Careers
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-173">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        <main className="pt-32 pb-16 px-6" data-id="element-174">
          <div className="max-w-4xl mx-auto" data-id="element-175">
            {/* 1. HEADER */}
            <div className="mb-16 space-y-4" data-id="element-176">
              <h1 className="text-4xl md:text-5xl font-serif leading-tight text-[#F5F5F5]" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-177">
                Your Archive
              </h1>
              <p className="text-base text-gray-500" data-id="element-178">
                Previously unlocked scans.
              </p>
            </div>

            {/* 2. LIST OF SCANS or EMPTY STATE */}
            {isLoading ? <div className="py-20 text-center" data-id="element-179">
                <p className="text-sm text-gray-600 font-mono" data-id="element-180">Loading...</p>
              </div> : scans.length === 0 /* 3. EMPTY STATE */ ? <div className="py-20 text-center space-y-8" data-id="element-181">
                <div className="space-y-4" data-id="element-182">
                  <p className="text-lg text-gray-400" data-id="element-183">
                    Your archive is empty.
                  </p>
                  <p className="text-base text-gray-500" data-id="element-184">
                    Run a scan to generate your first diagnostic.
                  </p>
                </div>
                <Link to="/reports" className="inline-flex items-center gap-3 text-base text-[#F5F5F5] hover:text-gray-400 transition-colors group" data-id="element-185">
                  <span data-id="element-186">Go to Reports Menu</span>
                  <span className="text-gray-600 group-hover:text-gray-400 transition-colors" data-id="element-187">
                    →
                  </span>
                </Link>
              </div> /* LIST OF SCANS */ : <div className="space-y-4" data-id="element-188">
                {scans.map(scan => <Link key={scan.id} to={getReportPath(scan)} className="block group" data-id="element-189">
                    <div className="p-6 border border-[#2B2B2B] bg-[#0A0A0A] hover:border-[#64748B] transition-colors duration-300" data-id="element-190">
                      <div className="flex items-start justify-between gap-4" data-id="element-191">
                        <div className="flex items-start gap-4 flex-1" data-id="element-192">
                          <span className="text-2xl" data-id="element-193">
                            {getTypeIcon(scan.type)}
                          </span>
                          <div className="space-y-2 flex-1" data-id="element-194">
                            <div className="flex items-center gap-3" data-id="element-195">
                              <p className="text-xs font-mono text-gray-600 uppercase tracking-wider" data-id="element-196">
                                {getTypeLabel(scan.type)}
                              </p>
                              <span className="text-xs text-gray-700" data-id="element-197">•</span>
                              <p className="text-xs font-mono text-gray-600" data-id="element-198">
                                {formatDate(scan.dateGenerated)}
                              </p>
                            </div>
                            <p className="text-base text-[#F5F5F5]" data-id="element-199">
                              {scan.name}
                            </p>
                            <p className="text-xs text-gray-600 font-mono uppercase tracking-wider" data-id="element-200">
                              Status: Full
                            </p>
                          </div>
                        </div>
                        <span className="text-gray-600 group-hover:text-gray-400 transition-colors text-sm font-mono" data-id="element-201">
                          →
                        </span>
                      </div>
                    </div>
                  </Link>)}
              </div>}
          </div>
        </main>

        {/* Footer */}
        <footer className="py-12 px-6 border-t border-[#2B2B2B]" data-id="element-202">
          <div className="max-w-6xl mx-auto" data-id="element-203">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8" data-id="element-204">
              <div data-id="element-205">
                <p className="text-sm text-gray-500 font-mono" data-id="element-206">
                  Lazy Monk Labs
                </p>
                <p className="text-xs text-gray-600 mt-1" data-id="element-207">
                  Pattern recognition systems
                </p>
              </div>

              <div className="flex gap-8 text-sm text-gray-500" data-id="element-208">
                <Link to="/decode" className="hover:text-[#F5F5F5] transition-colors" data-id="element-209">
                  Decoder
                </Link>
                <Link to="/careers" className="hover:text-[#F5F5F5] transition-colors" data-id="element-210">
                  Careers
                </Link>
                <Link to="/learnmore/founder" className="hover:text-[#F5F5F5] transition-colors" data-id="element-211">
                  Founder
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="hover:text-[#F5F5F5] transition-colors" data-id="element-212">
                  Contact
                </a>
              </div>

              <div className="text-xs text-gray-600 font-mono" data-id="element-213">
                © {new Date().getFullYear()}
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>;
}